import { Card, Table, Button, message, Tag, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductsByApp } from '@/api/apps';
import { 
  integrateProductToApp, 
  removeProductIntegration,
  getAllProductsForBinding 
} from '@/api/integrations';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { Product } from '@/types/product';
// 导入图标
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

/**
 * App 产品绑定管理页面
 */
export const AppProductsBinding = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 获取已绑定的产品
  const boundProductsQuery = useQuery({
    queryKey: ['app-bound-products', developerId, appId],
    queryFn: () => {
      if (!appId) throw new Error('App ID 不能为空');
      return getProductsByApp(appId);
    },
    enabled: !!appId && !!developerId,
  });

  // 获取所有可绑定的产品
  const allProductsQuery = useQuery({
    queryKey: ['all-products-for-binding', developerId],
    queryFn: getAllProductsForBinding,
    enabled: !!developerId,
  });

  const boundProducts = boundProductsQuery.data || [];
  const allProducts = allProductsQuery.data || [];
  const loading = boundProductsQuery.isLoading || allProductsQuery.isLoading;
  const error = boundProductsQuery.error || allProductsQuery.error;

  // 绑定产品 Mutation
  const bindMutation = useMutation({
    mutationFn: (productId: string) => {
      if (!appId) throw new Error('App ID 不能为空');
      return integrateProductToApp(appId, productId);
    },
    onSuccess: () => {
      message.success('产品集成成功');
      queryClient.invalidateQueries({ queryKey: ['app-bound-products', developerId, appId] });
    },
    onError: () => {
      message.error('产品集成失败');
    }
  });

  // 解绑产品 Mutation
  const unbindMutation = useMutation({
    mutationFn: (productId: string) => {
      if (!appId) throw new Error('App ID 不能为空');
      return removeProductIntegration(appId, productId);
    },
    onSuccess: () => {
      message.success('产品解除集成成功');
      queryClient.invalidateQueries({ queryKey: ['app-bound-products', developerId, appId] });
    },
    onError: () => {
      message.error('产品解除集成失败');
    }
  });

  /**
   * 处理返回 App 详情
   */
  const handleBack = (): void => {
    if (appId) {
      navigate(`/apps/${appId}`);
    }
  };

  /**
   * 处理绑定产品
   */
  const handleBind = (productId: string): void => {
    Modal.confirm({
      title: '确认集成',
      content: '确定要将该产品集成到当前App吗？',
      okText: '集成',
      cancelText: '取消',
      onOk: () => bindMutation.mutate(productId),
    });
  };

  /**
   * 处理解绑产品
   */
  const handleUnbind = (productId: string): void => {
    Modal.confirm({
      title: '确认解除集成',
      icon: <DeleteOutlined className="text-red-500" />,
      content: '确定要解除该产品与当前App的集成吗？',
      okText: '解除',
      cancelText: '取消',
      onOk: () => unbindMutation.mutate(productId),
    });
  };

  // 创建产品 ID 集合用于快速查找
  const boundProductIds = new Set(boundProducts.map(p => p.id));

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载产品绑定信息，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  // 表格列定义
  const columns: ColumnsType<Product> = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 220,
    },
    {
      title: '产品ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id) => <span className="font-mono text-sm">{id}</span>,
    },
    {
      title: '类别',
      key: 'category',
      render: (_, record) => record.category?.name || <span className="text-gray-400">—</span>,
    },
    {
      title: '集成状态',
      key: 'status',
      width: 120,
      render: (_: unknown, record: Product) => (
        boundProductIds.has(record.id) ? (
          <Tag color="green" className="px-2">已集成</Tag>
        ) : (
          <Tag color="default" className="px-2">未集成</Tag>
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: Product) => (
        boundProductIds.has(record.id) ? (
          <Button 
            size="small" 
            danger 
            icon={<DeleteOutlined className="text-xs" />}
            onClick={() => handleUnbind(record.id)}
            loading={unbindMutation.isPending && unbindMutation.variables === record.id}
            className="hover:shadow-sm transition-shadow"
          >
            解除
          </Button>
        ) : (
          <Button 
            size="small" 
            type="primary"
            icon={<PlusOutlined className="text-xs" />}
            onClick={() => handleBind(record.id)}
            loading={bindMutation.isPending && bindMutation.variables === record.id}
            className="hover:shadow-sm transition-shadow"
          >
            集成
          </Button>
        )
      ),
    },
  ];

  return (
    <div className="w-full space-y-8">
      {/* 标题+操作区 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">产品集成管理</h1>
          <p className="text-gray-500 mt-1">管理当前App的产品集成关系，支持集成/解除操作。</p>
        </div>
        <Button 
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回详情
        </Button>
      </div>

      {/* 产品表格 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ padding: '0px' } as React.CSSProperties}
      >
        <Table
          dataSource={allProducts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `共 ${total} 个产品`
          }}
          bordered={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          style={{
            '--ant-table-row-hover-bg': '#f5faff',
            '--ant-table-header-text-color': '#667085',
            '--ant-table-border-color': '#e8e8e8'
          } as React.CSSProperties}
          locale={{ emptyText: '暂无产品数据，先去创建产品吧。' }}
        />
      </Card>
    </div>
  );
};